import { useCallback, useMemo } from 'react'
import { BiLogOutCircle } from 'react-icons/bi'
import { Waypoint } from 'react-waypoint'
import {
  Box,
  Flex,
  Icon,
  Skeleton,
  Slide,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { isBoolean, isEmpty, isString } from 'lodash'
import simplur from 'simplur'

import { FormAuthType, FormColorTheme } from '~shared/types/form/form'

import { BxsTimeFive } from '~assets/icons/BxsTimeFive'
import { LOGGED_IN_KEY } from '~constants/localStorage'
import { useLocalStorage } from '~hooks/useLocalStorage'
import { getWso2LogoutSettings, logout } from '~services/CitizenAuthService'
import Button from '~components/Button'

import { usePublicAuthMutations } from '~features/public-form/mutations'
import { usePublicFormContext } from '~features/public-form/PublicFormContext'

const useFormHeader = () => {
  const { form, spcpSession, formId, submissionData, miniHeaderRef } =
    usePublicFormContext()
  const { handleLogoutMutation } = usePublicAuthMutations(formId)
  const [isAuthenticated, setIsAuthenticated] =
    useLocalStorage<boolean>(LOGGED_IN_KEY)

  const titleColour = useMemo(() => {
    if (form?.startPage.colorTheme === FormColorTheme.Orange) {
      return 'secondary.700'
    }
    return 'white'
  }, [form?.startPage.colorTheme])

  const titleBg = useMemo(
    () =>
      form?.startPage.colorTheme
        ? `theme-${form.startPage.colorTheme}.500`
        : `neutral.200`,
    [form?.startPage.colorTheme],
  )

  const estTimeString = useMemo(() => {
    if (!form?.startPage.estTimeTaken) return ''
    return simplur`${form?.startPage.estTimeTaken} min[|s] estimated time to complete`
  }, [form])

  const handleLogout = useCallback(() => {
    if (!form || form?.authType === FormAuthType.NIL) return
    if (form?.authType === FormAuthType.WSO2) {
      return makeLogoutWithWso2().then(() => {
        // Client-side logout.
        setIsAuthenticated(false)
      })
    }
    return handleLogoutMutation.mutate(form.authType)
  }, [form, handleLogoutMutation])

  return {
    title: form?.title,
    estTimeString,
    titleBg,
    titleColour,
    loggedInId: isAuthenticated ? spcpSession?.userName : undefined,
    showHeader: !submissionData,
    miniHeaderRef,
    handleLogout,
  }
}

// Logout from WSO2 IAS
const makeLogoutWithWso2 = async () => {
  try {
    // Get logout options
    const result = await getWso2LogoutSettings()
    if (
      result &&
      isString(result.endpoint) &&
      !isEmpty(result.endpoint) &&
      isString(result.wso2UserIdToken) &&
      !isEmpty(result.wso2UserIdToken) &&
      isString(result.logoutRedirectUrl) &&
      !isEmpty(result.logoutRedirectUrl) &&
      isBoolean(result.clientLogoutOnly) &&
      result.clientLogoutOnly === false &&
      isBoolean(result.logoutWithExtraTab)
    ) {
      const logoutUrl = `${result.endpoint}/oidc/logout?id_token_hint=${result.wso2UserIdToken}&post_logout_redirect_uri=${result.logoutRedirectUrl}`
      if (result.logoutWithExtraTab === true) {
        // Open the logout in a separate tab
        window.open(logoutUrl, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = logoutUrl
      }
    }
    // Malformed url, perform client logout
  } catch (error) {
    // Error for settings. Ignore
  } finally {
    // Perform client logout
    try {
      await logout()
    } catch (error) {
      // If logout failed from backend. Cookies may not have cleared. Ignore
    }
  }
}

export interface MiniHeaderProps {
  isOpen: boolean
}

// Exported for testing.
export const MiniHeader = ({ isOpen }: MiniHeaderProps): JSX.Element | null => {
  const { title, titleBg, titleColour, showHeader, miniHeaderRef } =
    useFormHeader()

  if (!showHeader) return null

  return (
    <Slide
      // Screen readers do not need to know of the existence of this component.
      aria-hidden
      ref={miniHeaderRef}
      direction="top"
      in={isOpen}
      style={{ zIndex: 10 }}
    >
      <Box bg={titleBg} px="2rem" py="1rem">
        <Skeleton isLoaded={!!title}>
          <Text as="h2" textStyle="h2" textAlign="start" color={titleColour}>
            {title ?? 'Loading title'}
          </Text>
        </Skeleton>
      </Box>
    </Slide>
  )
}

export const FormHeader = (): JSX.Element | null => {
  const {
    title,
    estTimeString,
    titleBg,
    titleColour,
    loggedInId,
    handleLogout,
    showHeader,
  } = useFormHeader()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const handlePositionChange = useCallback(
    (pos: Waypoint.CallbackArgs) => {
      // Required so a page that loads in the middle of the page can still
      // trigger the mini header.
      if (pos.currentPosition === 'above') {
        onOpen()
      } else {
        onClose()
      }
    },
    [onClose, onOpen],
  )

  if (!showHeader) return null

  return (
    <>
      <MiniHeader isOpen={isOpen} />
      <Flex
        px={{ base: '1.5rem', md: '3rem' }}
        py={{ base: '2rem', md: '3rem' }}
        justify="center"
        bg={titleBg}
      >
        <Flex
          maxW="57rem"
          flexDir="column"
          align={{ base: 'start', md: 'center' }}
          color={titleColour}
        >
          <Skeleton isLoaded={!!title}>
            <Text
              as="h1"
              textStyle="h1"
              textAlign={{ base: 'start', md: 'center' }}
            >
              {title ?? 'Loading title'}
            </Text>
          </Skeleton>
          {estTimeString && (
            <Flex align="flex-start" justify="center" mt="0.875rem">
              <Icon as={BxsTimeFive} fontSize="1.5rem" mr="0.5rem" />
              <Text textStyle="body-2" mt="0.125rem">
                {estTimeString}
              </Text>
            </Flex>
          )}
          {/* {loggedInId ? (
            <Button
              mt="2.25rem"
              variant="reverse"
              aria-label="Log out"
              rightIcon={<BiLogOutCircle fontSize="1.5rem" />}
              onClick={handleLogout}
            >
              {loggedInId} - Sign out
            </Button>
          ) : null} */}
        </Flex>
      </Flex>
      {/* Sentinel to know when sticky navbar is starting */}
      <Waypoint topOffset="64px" onPositionChange={handlePositionChange} />
    </>
  )
}
